<template>
  <div
    v-if="!comment.isUnsupportedIdeaAudit"
    class="comment"
    :class="{ 'comment--pending': isPending }"
  >
    <div class="comment__header">
      <CommentOwner
        v-if="comment.owner && !isAiFeedbackReplyComment"
        :user="comment.owner"
        class="comment__user"
        userNameWeight="400"
      >
        <template #username="{ userName }">
          {{ userName }} <span class="comment__action">{{ comment.subtitleLabel }}</span>
        </template>
      </CommentOwner>

      <div class="comment__subheader">
        <button
          v-if="isOpen"
          type="button"
          class="comment__button"
          @click="emit('click')"
        >
          <icon
            data="@icon/external-link.svg"
            :fill="false"
            color="#7D92A7"
            width="16"
            height="16"
          />
        </button>
        <DropdownTooltip
          v-if="
            commentOwner &&
            !comment.deleted &&
            !isReadonly &&
            (comment.isRegularComment || isStatusChangeComment)
          "
          placement="bottom-end"
          iconSize="sm"
          class="comment__header-menu"
        >
          <template #tooltip-content="{ hide }">
            <BaseSubHeading
              size="large"
              weight="400"
            >
              <button
                v-for="action in headerMenuActions"
                :key="action.id"
                type="button"
                class="dropdownTooltip_edit"
                @click.stop.prevent="action.handler(hide)"
              >
                {{ action.label }}
              </button>
            </BaseSubHeading>
          </template>
        </DropdownTooltip>
      </div>
    </div>

    <div
      v-click-outside="enter"
      class="comment__body"
      :class="{ 'is-editing': editComment, 'is-review-status': isReviewStatusComment }"
    >
      <div
        v-if="isAiFeedbackReplyComment"
        class="comment__ai-feedback-header"
      >
        <Avatar
          :src="aiFeedbackPictureUrl"
          :gap="aiFeedbackInitials"
          type="avatarGroup"
          class="comment__ai-feedback-avatar"
        />
        <BaseSubHeading
          size="medium"
          weight="400"
          color="monochrome-06"
        >
          {{ aiFeedbackName }}
          <span class="comment__action"
            >replied to {{ comment.repliedOnComment?.owner.fullName }}</span
          >
        </BaseSubHeading>
      </div>
      <div
        v-if="hasMedia"
        class="comment__files"
        :style="commentMediaCount === 1 ? { 'max-width': '260px' } : {}"
      >
        <div
          v-for="item in mediaFiles"
          :key="item.id"
          class="comment__file-wrapper"
          @click.capture="onDemoVideoClick($event, isDemoVideoAuditComment && !item.deleted)"
        >
          <div
            v-if="editComment"
            class="comment__box-shadow"
          ></div>
          <FileCommentItem
            :item="item"
            :isRemoved="item.deleted"
            :projectScoped="projectScoped"
          />
          <button
            v-if="
              editComment &&
              commentOwner &&
              comment.isRegularComment &&
              !item.deleted &&
              !isReadonly
            "
            type="button"
            class="comment__file-remove"
            @click.stop="deleteFile(item)"
          >
            <icon
              data="@icon/close.svg"
              :fill="false"
              width="var(--icon-16)"
              height="var(--icon-16)"
            />
          </button>
        </div>
        <div
          v-for="(videoUrl, index) in linkedVideoUrls"
          :key="`${comment.id}-linked-video-${index}`"
          class="comment__file-wrapper"
          @click.capture="
            onDemoVideoClick($event, isDemoVideoAuditComment && videoUrl === demoVideoUrl)
          "
        >
          <div
            v-if="editComment"
            class="comment__box-shadow"
          ></div>
          <div class="comment__file">
            <VideoPreview
              :playerId="`${comment.id}-linked-video-${index}`"
              :videoUrl="videoUrl"
              class="comment__file-video-preview"
            />
          </div>
        </div>
        <div
          v-if="deletedDemoVideoAttachment"
          class="comment__file-wrapper"
        >
          <div class="comment__file">
            <VideoAttachmentItem
              :file="deletedDemoVideoAttachment"
              :hideInfo="true"
              :hidePoster="true"
              fancyboxData="fancybox_comment"
              :useSharedFancybox="true"
              class="comment__file-video"
            />
          </div>
        </div>
      </div>

      <div
        class="comment__content"
        :class="{ 'comment__without-media': !hasMedia }"
      >
        <REGULAR_COMMENT
          v-if="comment.isRegularComment"
          :key="comment.fileMetadata?.length"
          :comment="comment"
          :targetConfig="targetConfig"
          :referenceFileTargetConfig="targetConfig"
          :editing="editComment"
          :pendingEditHtml="pendingEditHtml"
          :pendingEditJson="pendingEditJson"
          :pendingEditFiles="pendingEditFiles"
          :triggerFocus="editComment"
          :commentOwner="commentOwner"
          :isReadonly="isReadonly"
          :progress="progress"
          :comments="comments"
          :showDemoVideoTimestampEditor="showDemoVideoTimestampEditor"
          :demoVideoVersionMap="demoVideoVersionMap"
          :projectScoped="projectScoped"
          :referencesEnabled="referencesEnabled"
          @onEnter="enter"
          @onEditComment="onEditDescription"
          @onEditDemoVideoTimestamp="onEditDemoVideoTimestamp"
          @onCancelEdit="cancelEdit"
          @expandEditor="expandEditor"
          @deleteFile="deleteFile"
          @removePendingFile="emit('removePendingFile', $event)"
          @goToRepliedComment="goToRepliedComment"
          @editComment="onOpenEditComment(comment.id)"
          @demoVideoTimestampSelect="emit('demoVideoTimestampSelect', $event)"
        />
        <component
          :is="commentContentComponent"
          v-else-if="isStatusChangeComment"
          :comment="comment"
          :targetConfig="targetConfig"
          :referenceFileTargetConfig="targetConfig"
          :editing="editComment"
          :pendingEditHtml="pendingEditHtml"
          :pendingEditJson="pendingEditJson"
          :triggerFocus="editComment"
          :projectScoped="projectScoped"
          :referencesEnabled="referencesEnabled"
          @onEnter="enter"
          @onEditComment="onEditDescription"
          @onCancelEdit="cancelEdit"
          @expandEditor="expandEditor"
        />
        <component
          :is="commentContentComponent"
          v-else-if="!isDemoVideoAuditComment"
          :comment="comment"
          :demoVideoVersionMap="demoVideoVersionMap"
          :projectScoped="projectScoped"
        />
        <div
          class="comment__footer"
          :class="{ 'no-text': !hasText }"
        >
          <div class="comment__footer__left">
            <ReactionList
              :reactions="comment.reactionsWithUserPreviews"
              :myReactionType="myReactionType"
              @onSelectReaction="onSelectReaction"
            />
          </div>
          <div class="comment__footer__right">
            <div
              :key="commentCount"
              v-tooltip.bottom="{ content: comment.timeAndDate, theme: 'info-tooltip' }"
              class="comment__date"
            >
              {{ comment[date] }}
            </div>
            <template
              v-if="!comment.deleted && !comment.commentAuditRecord && !isDemoVideoAuditComment"
            >
              <EmojiDropdown
                class="comment__reaction-menu"
                :class="{ 'pointer-none': isReadonly }"
                :hideAfterSelect="true"
                @onSelect="emoji => onSelectReaction(emoji.type)"
              />
              <button
                v-if="showReplyButton"
                type="button"
                v-tooltip.top="{ content: 'Reply', theme: 'info-tooltip' }"
                class="comment__replay-button"
                :class="{ 'pointer-none': isReadonly }"
                @click.stop.prevent="replyComment(comment)"
              >
                <icon
                  data="@icon/reply-icon.svg"
                  :fill="true"
                  width="var(--icon-16)"
                  height="1em"
                />
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { directive as vClickOutside } from "click-outside-vue3"
import { computed, ref } from "vue"
import { useStore } from "vuex"

import Avatar from "@/components/Avatar/index.vue"
import DropdownTooltip from "@/components/Dropdown/DropdownTooltip.vue"
import EmojiDropdown from "@/components/Emoji/EmojiDropdown.vue"
import ReactionList from "@/components/Emoji/ReactionList.vue"
import FileCommentItem from "@/components/File/FileCommentItem.vue"
import CommentOwner from "@/components/User/index.vue"
import VideoAttachmentItem from "@/components/VideoPlayer/VideoAttachmentItem.vue"
import VideoPreview from "@/components/VideoPlayer/VideoPreview.vue"

import { isDemoVideoIdDeleted } from "@/composables/useDemoVideo"
import { extractVideoUrls } from "@/helpers/extractVideoUrls"
import { isImage } from "@/helpers/isImg"
import { isVideo } from "@/helpers/isVideo"
import {
  AIProfile,
  Comment,
  CommentContentType,
  EmojiType,
  FileMetaData,
  reviewStatuses,
  User,
  VUFile
} from "@/models"
import { Getters } from "@/store"
import { getInitials } from "@/utils/helpers"

import commentContentComponents from "./CommentContent/index"

import type { DemoVideoVersionInfo } from "@/composables/useDemoVideo"
import type { ItemAuditDemoFile, ItemStatus } from "@/models"

interface Props {
  comment: Comment
  comments?: Comment[]
  isOpen?: boolean
  editComment?: boolean
  pendingEditHtml?: string
  pendingEditJson?: string
  pendingEditFiles?: VUFile[]
  date?: string
  owner?: string
  isReadonly?: boolean
  progress?: { [key: string]: number }
  isPending?: boolean
  showDemoVideoTimestampEditor?: boolean
  demoVideoVersionMap?: Record<string, DemoVideoVersionInfo>
  projectScoped?: boolean
  referencesEnabled?: boolean
}

defineOptions({ name: "CommentItem" })

const props = withDefaults(defineProps<Props>(), {
  comments: () => [],
  isOpen: false,
  editComment: false,
  pendingEditHtml: "",
  pendingEditJson: "",
  pendingEditFiles: () => [],
  date: "timeAgo",
  owner: "",
  isReadonly: false,
  progress: () => ({}),
  isPending: false,
  showDemoVideoTimestampEditor: false,
  projectScoped: true,
  referencesEnabled: true,
  demoVideoVersionMap: () => ({})
})

const emit = defineEmits([
  "click",
  "commentMenu",
  "deleteFile",
  "removePendingFile",
  "enter",
  "expandEditor",
  "goToRepliedComment",
  "onEditComment",
  "onEditDemoVideoTimestamp",
  "onOpenEditComment",
  "onSelectReaction",
  "replyComment",
  "demoVideoTimestampSelect"
])

const store = useStore()
const commentClone = ref<Comment | null>(null)
const REGULAR_COMMENT = commentContentComponents.REGULAR_COMMENT

const currentUser = computed<User>(() => store.getters[Getters.GET_USER_CURRENT_PROFILE])
const aiProfile = computed<AIProfile>(() => store.getters[Getters.GET_AI_PROFILE])
const commentCount = computed(() => store.getters[Getters.GET_COMMENT_COUNT])
const commentContentComponent = computed(
  () => commentContentComponents[props.comment.contentComponentName] ?? REGULAR_COMMENT
)

const isDemoVideoComment = computed(() => props.comment.type === CommentContentType.DEMO_VIDEO)
const isStatusChangeComment = computed(() => props.comment.type === CommentContentType.STATUS)
const isAiFeedbackReplyComment = computed(
  () => !!props.comment.repliedOnComment?.id && props.comment.source?.sourceType === "AI_FEEDBACK"
)
const aiFeedbackName = computed(() => aiProfile.value.name || "AI Feedback")
const aiFeedbackInitials = computed(() => getInitials(aiFeedbackName.value))
const aiFeedbackPictureUrl = computed(() => aiProfile.value.picture?.pictureUrl ?? null)
const showReplyButton = computed(
  () =>
    props.comment.isRegularComment ||
    isStatusChangeComment.value ||
    !props.comment.source ||
    (["ITEM_STATUS_CHANGE", "IDEA_STATUS_CHANGE"].includes(props.comment.source.sourceType) &&
      props.comment.source.sourceId)
)
const isDemoVideoAuditComment = computed(
  () => isDemoVideoComment.value && !!props.comment.itemAuditRecord
)

const hasDemoFile = (demoFile?: ItemAuditDemoFile | null) =>
  Boolean(demoFile?.demoVideoUrl?.trim() || demoFile?.demoFileMetadata?.id)

const beforeDemoFile = computed(() => props.comment.before?.demoFile ?? null)

const afterDemoFile = computed(() => props.comment.after?.demoFile ?? null)

const isDeletedDemoVideo = computed(
  () =>
    isDemoVideoAuditComment.value &&
    hasDemoFile(beforeDemoFile.value) &&
    !hasDemoFile(afterDemoFile.value)
)

const isDemoVideoDeleted = computed(() =>
  isDemoVideoIdDeleted(props.comment.demoVideoId, props.demoVideoVersionMap)
)

const demoFile = computed(() => {
  if (!isDemoVideoAuditComment.value) return null

  return hasDemoFile(afterDemoFile.value) ? afterDemoFile.value : beforeDemoFile.value
})

const demoAttachment = computed(() => {
  if (!isDemoVideoAuditComment.value) return null

  const freshFileMetadata = props.comment.fileMetadata?.[0]
  if (freshFileMetadata?.id) {
    return FileMetaData.create({
      ...freshFileMetadata,
      deletedBy: freshFileMetadata.deletedBy ?? props.comment.owner
    })
  }

  const fileMetadata = demoFile.value?.demoFileMetadata
  if (!fileMetadata?.id) return null

  return FileMetaData.create({
    ...fileMetadata,
    deleted: isDeletedDemoVideo.value || isDemoVideoDeleted.value,
    deletedBy: fileMetadata.deletedBy ?? props.comment.owner
  })
})

const demoVideoUrl = computed(() => afterDemoFile.value?.demoVideoUrl?.trim() ?? "")

const deletedDemoVideoUrl = computed(() => {
  if (!isDeletedDemoVideo.value) return ""

  return beforeDemoFile.value?.demoVideoUrl?.trim() ?? ""
})

const deletedDemoVideoAttachment = computed(() => {
  if (!deletedDemoVideoUrl.value) return null

  return FileMetaData.create({
    id: `${props.comment.id}-deleted-demo-video-url`,
    owner: props.comment.owner ?? new User(),
    name: deletedDemoVideoUrl.value,
    size: 0,
    createdDate: "",
    targetType: "",
    deleted: true,
    deletedBy: props.comment.owner
  })
})

const mediaFiles = computed(() => {
  const files = isDemoVideoAuditComment.value
    ? []
    : (props.comment.fileMetadata?.filter(item => isImage(item.name) || isVideo(item.name)) ?? [])

  return demoAttachment.value ? [...files, demoAttachment.value] : files
})

const commentOwner = computed(() => props.comment.owner?.id === props.owner)

const targetConfig = computed(() => {
  if (!props.comment.targetResourceId?.id || !props.comment.targetResourceId?.type) {
    return null
  }

  return {
    type: props.comment.targetResourceId.type,
    targetId: props.comment.targetResourceId.id
  }
})

const linkedVideoUrls = computed(() => {
  const html = props.comment.structuredDescription?.html
  const text = props.comment.structuredDescription?.text

  return [
    ...extractVideoUrls(html || text || ""),
    ...(demoVideoUrl.value ? [demoVideoUrl.value] : [])
  ]
})

const commentMediaCount = computed(
  () =>
    (mediaFiles.value?.length ?? 0) +
    linkedVideoUrls.value.length +
    (deletedDemoVideoAttachment.value ? 1 : 0)
)

const hasMedia = computed(() => {
  if (commentMediaCount.value) {
    return true
  }

  if (!mediaFiles.value) {
    return false
  }

  return mediaFiles.value.some(file => file.isImage || file.isVideo)
})

const myReactionType = computed(() => {
  for (const [reactionType, users] of Object.entries(props.comment.reactionsWithUserPreviews)) {
    if (users.some(user => user.id === currentUser.value.id)) {
      return reactionType as EmojiType
    }
  }
  return null
})

const hasText = computed(() => {
  const html = props.comment.structuredDescription?.html || ""
  const text = html.replace(/<[^>]*>/g, "").trim()
  return !!text
})

const isReviewStatusComment = computed(() => {
  return (
    reviewStatuses.includes((props.comment.after?.status ?? "") as ItemStatus) &&
    props.comment.assignees.length > 0
  )
})

function onEditDescription(e) {
  onEditComment({
    field: "structuredDescription",
    value: {
      text: e.getEmailText(),
      html: e.html,
      json: JSON.stringify(e.json)
    }
  })
}

function onEditDemoVideoTimestamp(value: Comment["demoVideoTimestamp"]) {
  emit("onEditDemoVideoTimestamp", {
    field: "demoVideoTimestamp",
    value,
    commentId: props.comment.id
  })
}

function goToRepliedComment(commentId: string) {
  emit("goToRepliedComment", commentId)
}

function onDemoVideoClick(event: MouseEvent, isDemoVideo: boolean) {
  if (!isDemoVideo || isDemoVideoDeleted.value) return

  event.preventDefault()
  event.stopPropagation()
  emit("demoVideoTimestampSelect")
}

function onEditComment({ field, value }) {
  emit("onEditComment", {
    field,
    value,
    commentId: props.comment.id
  })
}

function expandEditor() {
  emit("expandEditor")
}

function cancelEdit() {
  if (commentClone.value) {
    onEditComment({
      field: "structuredDescription",
      value: {
        html: commentClone.value.structuredDescription.html,
        text: commentClone.value.structuredDescription.text,
        json: commentClone.value.structuredDescription.json
      }
    })
    commentClone.value = null
  }
  onOpenEditComment("")
}

function onOpenEditComment(commentId: string) {
  if (props.isReadonly) {
    emit("onOpenEditComment", "")
    return
  }

  commentClone.value = Comment.clone(props.comment)
  emit("onOpenEditComment", commentId)
}

function enter() {
  commentClone.value = null
  if (props.editComment) {
    emit("enter", {
      comment: props.comment
    })
  }
}

function onSelectReaction(reactionType: EmojiType | null) {
  if (props.isReadonly) return

  emit("onSelectReaction", {
    commentId: props.comment.id,
    reactionType: myReactionType.value === reactionType ? null : reactionType
  })
}

function deleteFile(file: FileMetaData) {
  emit("deleteFile", {
    file,
    commentId: props.comment.id
  })
}

function replyComment(comment: Comment) {
  if (props.isReadonly) return

  emit("replyComment", comment)
}

function handleEditComment(hide: () => void) {
  if (props.isReadonly) {
    hide()
    return
  }

  onOpenEditComment(props.comment.id)
  hide()
}

function handleDeleteComment(hide: () => void) {
  if (props.isReadonly) {
    hide()
    return
  }

  emit("commentMenu", { comment: props.comment, value: "delete" })
  hide()
}

const headerMenuActions = computed(() => [
  ...(isDemoVideoDeleted.value ? [] : [{ id: "edit", label: "Edit", handler: handleEditComment }]),
  ...(props.comment.isRegularComment
    ? [{ id: "delete", label: "Delete", handler: handleDeleteComment }]
    : [])
])
</script>
